/**
 * Rebuild a FULL analysis request for the "Re-run Analysis" button.
 *
 * THE BUG THIS EXISTS TO KILL. Re-run used to post six fields — address, strategy,
 * user_id, language and a hardcoded property_condition of 'average'. Everything the
 * user had confirmed was simply absent from the request, so the backend re-derived the
 * subject from the data provider and filled the rest from its own fallbacks. Measured
 * against the live API on 553 Belmont, a confirmed duplex bought at $89,000:
 *
 *     six-field payload -> Single Family, $35,000, 1,913 sqft, 1.0 bath, ARV 241,300
 *     full payload      -> Duplex,        $89,000, 2,204 sqft, 2.0 bath, ARV 227,428
 *
 * Same address, same endpoint, same minute. A button labelled "get the latest
 * valuation" was valuing a different property.
 *
 * The rule is the one already applied to rentalFinancingType in Results.js: what the
 * user confirmed survives, and a default never silently takes its place. Sources are
 * the analysis snapshot (the confirmed subject) and the live assumptions (their edits
 * on the results screen), mirroring the payload PropertyConfirmation.js builds.
 *
 * Pure so it can be tested without mounting Results.
 */

const DEFAULT_CRITERIA = { min_profit: 20000, min_margin: 10 };

/** Parse a currency-ish value to a number, or null. '' / null / 'abc' -> null. */
export const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const readStoredCriteria = () => {
  try {
    const stored = typeof localStorage !== 'undefined'
      && localStorage.getItem('chekdeal_classification_criteria');
    if (stored) return JSON.parse(stored);
  } catch (e) { /* fall through to the defaults */ }
  return DEFAULT_CRITERIA;
};

export const buildRerunRequest = ({
  analysis,
  assumptions = {},
  user = null,
  lang = 'en',
  criteria,
} = {}) => {
  if (!analysis || !analysis.address) {
    throw new Error('buildRerunRequest: analysis.address is required');
  }

  const snap = analysis.property_snapshot || {};
  const saved = analysis.assumptions || {};
  const a = assumptions || {};
  const strategy = analysis.strategy || 'flip';
  const crit = criteria || readStoredCriteria();

  const req = {
    input_type: 'address',
    input_value: analysis.address,
    user_id: (user && user.user_id) || 'guest',
    strategy,
    language: lang,

    // The confirmed subject. Its absence is what reverted a duplex to Single Family.
    property_overrides: {
      property_type: snap.property_type,
      beds: snap.beds != null ? Number(snap.beds) : undefined,
      baths: snap.baths != null ? Number(snap.baths) : undefined,
      sqft: snap.sqft != null ? Number(snap.sqft) : undefined,
      year_built: snap.year_built != null ? Number(snap.year_built) : undefined,
    },

    rehab_condition: a.rehabCondition || 'auto',
    target_rule: (a.maoRule || 70) / 100,
    purchase_financing: a.purchaseFinancing || 'cash',
    rehab_financing: a.rehabFinancing || 'cash',
    min_profit: crit.min_profit || DEFAULT_CRITERIA.min_profit,
    min_margin: crit.min_margin || DEFAULT_CRITERIA.min_margin,
  };

  // Price. Without it the backend falls through to listing data or a literal.
  const price = toNumber(analysis.purchase_price);
  if (price && price > 0) req.asking_price = price;

  // property_condition is OMITTED unless we actually know it.
  //
  // The old payload pinned 'average' on every re-run, overwriting whatever the user
  // had chosen. We cannot simply restore the real one: the analyze response carries
  // property_condition nowhere — not in property_snapshot, not in assumptions, not at
  // the top level (verified against the live API). So the honest options are to invent
  // a value or to say nothing, and inventing one is the bug being fixed here. Saying
  // nothing lets the backend derive condition the same way it does when a first
  // analysis leaves it untouched. Recorded in BACKLOG.md: the response should persist
  // it, and until it does, a re-run cannot reproduce a hand-picked flip condition.
  const condition = snap.property_condition || saved.property_condition;
  if (condition) req.property_condition = condition;

  // Rehab stays authoritative, exactly as on the first run.
  const total = toNumber(a.customRehabTotal);
  const perSqft = toNumber(a.customRehabPerSqft);
  if (a.customRehabInputMode === 'persqft' && perSqft > 0) {
    req.custom_rehab_per_sqft = perSqft;
  } else if (total > 0) {
    req.custom_rehab_total = total;
  }

  // Multi-family income input — drives the income-led ARV in every strategy.
  const grm = toNumber(a.flipGrm);
  if (grm && grm > 0) req.multi_family_grm = grm;

  if (strategy === 'rental') {
    // unit_count comes from the SNAPSHOT deliberately: it is the count the original
    // run settled on, and user_rent is the total that was entered against that same
    // count. Sending a different one would re-divide the rent — see the unit_count
    // entry in BACKLOG.md. A re-run must reproduce the first run, not reinterpret it.
    Object.assign(req, {
      user_rent: toNumber(a.rentalMonthlyRent) ?? undefined,
      unit_count: snap.unit_count != null ? Number(snap.unit_count) : undefined,
      rental_financing_type: a.rentalFinancingType || 'dscr',
      rental_down_payment_pct: a.rentalDownPaymentPct,
      rental_interest_rate: a.rentalInterestRate,
      rental_loan_term_years: a.rentalLoanTermYears,
      rental_loan_points: a.rentalLoanPoints,
      rental_property_condition: a.rentalPropertyCondition || 'turnkey',
      rental_purchase_type: a.rentalPurchaseType || 'new_purchase',
      annual_property_taxes: toNumber(a.rentalAnnualTaxes) || 0,
      annual_insurance: toNumber(a.rentalAnnualInsurance) || 0,
      rental_closing_costs_pct: a.rentalClosingCostsPct,
      rental_origination_points: a.rentalOriginationPoints,
      rental_lender_fees: toNumber(a.rentalLenderFees) || 0,
      rental_title_attorney_fees: toNumber(a.rentalTitleAttorneyFees) || 0,
      appraised_value: toNumber(a.rentalAppraisedValue),
      existing_payoff: toNumber(a.rentalExistingPayoff) || 0,
      refi_ltv: a.rentalRefiLtv,
      tolerance_mode: a.toleranceMode,
    });
  }

  return req;
};

export default buildRerunRequest;
